import { useEffect, useRef, useState } from 'react'
import {
  mergeProfilesWithResponseImages,
  type RespondentProfile,
} from '../audienceProfiles'
import { studyReportClient } from '../studyReportClient'

export interface UseAudienceProfilesHydrationOptions {
  studyId: string
  survey: unknown
  responses: Array<any>
  enabled: boolean
}

export interface UseAudienceProfilesHydrationResult {
  /** Undefined while initial hydration is in progress (only when `enabled`). */
  profiles: Array<RespondentProfile> | undefined
  /** True only when `enabled` and profiles have not been resolved yet. */
  loading: boolean
  error: string | null
}

/**
 * Loads cached audience profiles for a study.
 */
export function useAudienceProfilesHydration(
  options: UseAudienceProfilesHydrationOptions,
): UseAudienceProfilesHydrationResult {
  const { studyId, responses, enabled } = options

  const [profiles, setProfiles] = useState<Array<RespondentProfile> | undefined>(
    undefined,
  )
  const [error, setError] = useState<string | null>(null)

  const runSeqRef = useRef(0)
  useEffect(() => {
    if (!enabled) {
      setProfiles(undefined)
      setError(null)
      return
    }

    const seq = ++runSeqRef.current
    let cancelled = false

    async function hydrate() {
      setError(null)
      setProfiles(undefined)

      try {
        const cached = await studyReportClient.getAudienceProfiles(studyId)
        if (cancelled || seq !== runSeqRef.current) return

        const profilesArray: Array<RespondentProfile> = Array.isArray(cached.profiles)
          ? cached.profiles
          : Array.isArray(cached.profiles?.profiles)
            ? cached.profiles.profiles
            : []
        if (profilesArray.length > 0) {
          setProfiles(mergeProfilesWithResponseImages(profilesArray, responses))
          return
        }

        setProfiles([])
      } catch (e: unknown) {
        if (cancelled || seq !== runSeqRef.current) return
        const message = e instanceof Error ? e.message : String(e)
        setError(message || 'Failed to load audience profiles')
        setProfiles([])
      }
    }

    void hydrate()

    return () => {
      cancelled = true
    }
  }, [enabled, studyId, responses])

  if (!enabled) {
    return { profiles: undefined, loading: false, error: null }
  }

  const loading = profiles === undefined

  return { profiles, loading, error }
}
